import React, { ChangeEvent, useEffect, useState } from 'react';
import axios from 'axios';
import { FormSectionElements, ProgramContainer } from '../../entities/formTemplateDataType';
import { AutoComplete, AutoCompleteChangeEvent, AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primereact/autocomplete';
import { ErrorMessage } from './AddressComponent';
import { useAddressSearch } from '../../services/AddressSearchHooks';
import AddressDetails from './AddressDetails';
import ErrorElement from '../../commonComponent/ErrorElement';
import { getAddress } from '../../utilities/addressUtils';
import { findMatchedAddressElement, getRootComponent } from '../../utilities/ElementUtils';
import FormValidationUtils from '../../utilities/FormValidationUtils';
import { add, get, set } from 'lodash';

export class Address {
    address1: string;
    address2?: string;
    streetName?: string;
    streetType?: string;
    streetNumber?: string;
    streetNumberSuffix?: string;
    streetDirection?: string;
    streetFromNumber?: string;
    streetToNumber?: string;
    streetFromSuffix?: string;
    streetToSuffix?: string;
    suiteNumber?: string;
    suiteFromNumber?: string;
    suiteToNumber?: string;
    municipality: string;
    directoryArea?: string;
    province: string;
    postalCode: string;
    stationName?: string;
    stationType?: string;
    poBoxKeyword?: string;
    poBoxNumber?: string;
    poBoxFromNumber?: string;
    poBoxToNumber?: string;
    routeType?: string;
    routeNumber?: string;
    deliveryInstallationArea?: string;
    deliveryInstallationName?: string;
    deliveryInstallationType?: string;
    typeOfAddress?: Number;
    mailAddressType?: number;
    unitType?: UNIT_TYPE;
    streetSequence?: string;
    type?: number;
    description?: string;
    lotType?: LOT_TYPE;
    lotNumber?: string;
    identifier?: PROPERTY_IDENTITFIER;
    identifierNumber?: string;
    township?: string;
    isNoStreetNoAddress?: boolean;
    isDetailAddress = true;
  };

export interface AddressSearchProps {
    lang: 'en' | 'fr';
    setFieldValue: (val: string) => void;
    fieldName: string;
    searchUrl: string;
    field: FormSectionElements;
    setFieldValid: (valid: boolean) => void;
    setFieldAddress: (address: Address) => void;
    setSameAsResidential: (value: boolean) => void;
    setValidationErrorMessage: (errors: ErrorMessage[]) => void;
    callback?: (programContainer: ProgramContainer)=> void;
    accessToken?: string
}

export type UNIT_TYPE = 'APT' | 'SUITE' | 'UNIT' | '';
export type LOT_TYPE = '' | 'L' | 'P' | 'B' | 'K';
export type PROPERTY_IDENTITFIER = 'CN' | 'PN' | '';

const propts = {
    streetNo: {en: 'Start typing any address or postal code', fr: 'Commencez à taper une adresse ou code postal'},
    noStreetNo: {en: 'Start typing any postal code', fr: 'Commencez à taper un code postal'},
    title: {en: 'My address does not have a street number or street name', fr: 'Mon adresse ne comporte ni numéro municipal ni nom de rue'},
    sameMaling: {en: 'Mailing address is same as residential', fr: 'L\'adresse postale est la même que l\'adresse résidentielle'},
  };

const AddressSearchComponent = ({
    lang,
    fieldName,
    searchUrl,
    field,
    setFieldValue,
    setFieldValid,
    setFieldAddress,
    setSameAsResidential,
    setValidationErrorMessage,
    callback,
    accessToken,
}: AddressSearchProps) => {
    const profile = field.parent.parent.parent.Profile;
    const isPCSearch = (field.Address?.isDetailAddress || profile?.address?.isDetailAddress) ? (field.Address?.isNoStreetNoAddress || profile?.address?.isNoStreetNoAddress) : false
    let addressString = (field.addressType === 'Mailing' && field.mailingSameAsResidential) || field.addressType==='Residential'
        ? ((isPCSearch ? (field.Address?.postalCode || profile?.address?.postalCode) : field.Value) 
            || getAddress(field.Address || profile?.address, field.Address?.isNoStreetNoAddress ?? profile?.address?.isNoStreetNoAddress, lang))
        : (field.Value || getAddress(field.Address, field.Address?.isNoStreetNoAddress, lang));
    const [timeoutIds, setTimeoutIds] = useState([]);
    const [value, setValue] = useState('');
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [noStreetNo, setNoStreeNo] = useState(isPCSearch);
    const [sameMailing, setSameMailing] = useState(field.mailingSameAsResidential);
    const [addressDetails, setAddressDetails] = useState<Address|null>(field?.Address?.isDetailAddress ? field.Address : null);
    const [errors, setErrors] = useState<ErrorMessage[]>([]);
    const { data, loading, error, setSearchingAddress } = useAddressSearch(
        addressString,
        process.env.PC_LOOKUP_URL|| searchUrl,
        addressDetails? addressDetails.isNoStreetNoAddress : profile?.address?.isDetailAddress ? profile.address.isNoStreetNoAddress : false,
        isPCSearch && profile?.address?.postalCode===addressString,
        accessToken,
    );
    const searchDelay = 1000;

    const getHeader = () => {
        let header: any = {
            "Content-Type": "application/json",
        }
        if (accessToken) {
            header = {...header, "Authorization": `Bearer ${accessToken}`}
        }
        return header;
    }

    const search = (e: AutoCompleteCompleteEvent) => {
        if (e.originalEvent.nativeEvent["inputType"] == "deleteContentBackward")
            return;
        const value = e.query;
        if (value?.length < 3) {
            return;
        }
        if (noStreetNo) {
            searchWithPostalCode(value);
        } else {
            searchWithAddress({
                "addressString": value,
                "province": "ON",
                "displayAddressLines": true
            })
        }
    }

    const searchWithAddress = (payload: {
        "addressString": string;
        "province": string,
        "displayAddressLines": boolean,
    }) => {
        const searchId = setTimeout(() => {
            try {
                axios.post(searchUrl, payload, {headers: getHeader()}).then(res=>{
                    const data = res.data; 
                    if (data.status === 'OK') {
                        if (data.mailAddressChoice?.addressDetails) {
                            setValue(data.mailAddressChoice.addressAlternatives[0])
                            setSuggestions([]);
                            setFieldValue(data.mailAddressChoice.addressAlternatives[0]);
                            setupAddressDetails(data.mailAddressChoice.addressDetails);
                        } else {
                            setSuggestions(data.mailAddressChoice?.addressAlternatives as string[]);
                        }
                    } 
                })
            } catch (error) {
                console.error("API call failed:", error);
            }
        }, searchDelay);
        const ids = [...timeoutIds];
        ids.push(searchId);
        setTimeoutIds(ids)
    };

    const searchWithPostalCode = (postalCode: string) => {
        const searchId = setTimeout(() => {
            const id = 'searchInput';
            try {
                if (postalCode === addressDetails?.postalCode) {
                    return;
                } 

                if (!postalCode) {
                    removeErrorMessage(id);
                }

                if (!/[a-z][0-9][a-z]\s*[0-9][a-z][0-9]/i.test(postalCode)) {
                    addErrorMessage(id, lang==='en'? 'Invalid postal code': 'Code postal invalide');
                } else {    
                    removeErrorMessage(id);
                    axios.post(process.env.POSTAL_CODE_LOOKUP_URL, {postalCode}, {headers: getHeader()}).then(res=>{
                        const data = res.data; 
                        if (data.status === 'OK') {
                            const address:Address = data.mailAddressChoice[0];
                            const addressVal = getAddress({...address, isDetailAddress: true, isNoStreetNoAddress: true}, false, lang); // Utils.getAddressStringFromDetails(address, true, lang);
                            //setSuggestions([`${address.municipality}, ${address.province}, ${address.postalCode}`]);
                            setupAddressDetails(address, true);
                            setValue(addressVal);
                            setFieldValue(addressVal);
                        } else {
                            addErrorMessage(id, lang === 'en' ? 'Postal code not found' : 'Code postal introuvable');
                        }
                    });
                }
            } catch (error) {
                console.error("API call failed:", error);
            }
        }, searchDelay);

        const ids = [...timeoutIds];
        ids.push(searchId);
        setTimeoutIds(ids)
    };

    const callbackParent = () => {
        const root = getRootComponent(field);
        if (root) {
            callback(root);
        }
    }

    const selectAddress = (event: AutoCompleteSelectEvent) => {
        //setValue(event.value);
        if (!noStreetNo) {
            setSearchingAddress(event.value);
        }
    }

    const AddressSearchSuggestionTemplate = (suggestion: string) => {
        return (
            <div key={suggestion} style={{width: '100%'}}>
                {suggestion}
            </div>
        )
    }

    const setupAddressDetails = (address: Address, isNoStreetNoAddress=false) => {
        let add = address;
        if (address.suiteNumber && !address.unitType) {
            add = {...address, unitType: 'UNIT' }
        }
        add = {...add, isDetailAddress: true, isNoStreetNoAddress};
        setAddressDetails(add);
    }

    const addErrorMessage = (id: string, message: string) => {
        const newErrors = errors.filter(err => err.id !==id);
        newErrors.push({id, message});
        setErrors(newErrors);
    }

    const removeErrorMessage = (id: string) => {
        setErrors(errors.filter(err => err.id !== id));
    };

    const validatNoStreetNoAddress = (addressDetails: Address, newErrors: ErrorMessage[]): ErrorMessage[] => {
        newErrors = validateLotType(addressDetails, newErrors);
        newErrors = validatePropertyIdentifier(addressDetails, newErrors);
        return newErrors;
    };
    
    const validatePropertyIdentifier = (addressDetails: Address, newErrors: ErrorMessage[]): ErrorMessage[] => {
        newErrors = newErrors.filter(err => err.id !== 'identifier' && err.id !== 'identifierNumber')

        if (addressDetails.identifier && !addressDetails.identifierNumber) {
            newErrors.push({id: 'identifierNumber', message: `${lang==='en' ? 'You must select either concession number or plan number' : 'Vous devez sélectionner soit le numéro de concession, soit le numéro de plan'}`});
        }
        if (!addressDetails.identifier) {
          newErrors.push({id: 'identifier', message: `${lang==='en' ? 'You must select either concession number or plan number' : 'Vous devez sélectionner soit le numéro de concession, soit le numéro de plan'}`});
        }
        return newErrors;
      }
    
    const validateLotType = (addressDetails: Address, newErrors: ErrorMessage[]): ErrorMessage[] => {
        newErrors = newErrors.filter(err => err.id !== 'lotType' && err.id !== 'lotNumber');
        if (!addressDetails.lotNumber) {
          newErrors.push({id: 'lotNumber', message: `${lang==='en' ? 'Enter your Lot number' : 'Entrez votre numéro de lot'}`});
        }
        if (!addressDetails.lotType) {
          newErrors.push({id: 'lotType', message: `${lang==='en' ? 'Select your Lot Type' : 'Entrez votre numéro de lot'}`});
        }
        return newErrors;
      }
    
    const validateUnitType = (addressDetails: Address, newErrors: {id: string, message: string}[] = []) => {
        newErrors = newErrors.filter(err => err.id !== 'unitType' && err.id !== 'suiteNumber');
        if (addressDetails.unitType && !addressDetails.suiteNumber) {
            newErrors.push({id: 'suiteNumber', message: `${lang==='en' ? 'You must enter unit number when select unit type': 'Vous devez sélectionner le type d’unité lorsque le champ du numéro d’unité n’est pas vide.'}`});
        }
        if (!addressDetails.unitType && addressDetails.suiteNumber) {
          newErrors.push({id: `unitType`, message: `${lang==='en' ? 'You must select unit type when unit number is not blank': 'Vous devez sélectionner un numéro d’unité lorsque le type d’unité est sélectionné.'}`})
        }

        if (addressDetails.unitType && addressDetails.suiteNumber) {
            const suiteNum = parseInt(addressDetails.suiteNumber);
            const suiteFrom = parseInt(addressDetails.suiteFromNumber);
            const suiteTo = parseInt(addressDetails.suiteToNumber);
        
            if (!isNaN(suiteNum) && ((!isNaN(suiteFrom) && suiteNum < suiteFrom) || (!isNaN(suiteTo) && suiteNum > suiteTo))) {
                newErrors.push({id: 'suiteNumber', message: `${lang==='en' ? 'Suite number is out of range' : 'Le numéro de suite est hors de portée'}`});
            }
        }
        return newErrors;
    }

    const validateRoute = (addressDetails: Address, newErrors: ErrorMessage[]): ErrorMessage[] => {
        newErrors = newErrors.filter(err => err.id !== 'routeType' && err.id !== 'routeNumber');
        if (addressDetails.routeType && !addressDetails.routeNumber) {
            newErrors.push({id: 'routeNumber', message: `${lang === 'en' ? 'You must enter route number when route type is not blank' : "Vous devez sélectionner le type de route lorsque le numéro de route n’est pas laissé vide."}`});
        }
        if (!addressDetails.routeType && addressDetails.routeNumber) {
            newErrors.push({id: 'routeType', message: `${lang === 'en' ? 'You must select route type when route number is not blank' : "Vous devez sélectionner le type d'itinéraire lorsque le numéro d'itinéraire n'est pas vide"}`});
        }
        return newErrors;
    }
    const validateTownship = (addressDetails: Address, newErrors: ErrorMessage[]): ErrorMessage[] => {
        newErrors = newErrors.filter(err => err.id !== 'township');
        if (!addressDetails.township) {
            newErrors.push({id: 'township', message: `${lang === 'en' ? 'Enter your Township' : 'Entrez votre canton'}`})
        }
        return newErrors;
    }
    
    const validatAddress = (addressDetails: Address, newErrors: ErrorMessage[]): ErrorMessage[] => {
        newErrors = newErrors.filter(err => err.id !== 'streetNumber' && err.id !== 'streetName')
        if (!addressDetails.streetNumber) {
            newErrors.push({id: 'streetNumber', message: `${lang==='en' ? 'You must enter street number' : 'Vous devez entrer le numéro de rue'}`});
        } else {
            const streetNum = parseInt(addressDetails.streetNumber);
            const streetFrom = parseInt(addressDetails.streetFromNumber);
            const streetTo = parseInt(addressDetails.streetToNumber);
        
            if (!isNaN(streetNum) && ((!isNaN(streetFrom) && streetNum < streetFrom) || (!isNaN(streetTo) && streetNum > streetTo))) {
                newErrors.push({id: 'streetNumber', message: `${lang==='en' ? 'Your entered street number is out of range' : 'Votre numéro de rue saisi est hors de portée'}`});
            }
        }
        if (!addressDetails.streetName) {
          newErrors.push({id: 'streetName', message: `${lang==='en' ? 'You must enter street name' : 'Vous devez entrer un nom de rue.'}`});
        }
        return newErrors;
    }
    
    const validatAddressDetails = (): ErrorMessage[] => {
        let newErrors: ErrorMessage[] = [];
        if (!addressDetails) {
          return [];
        }
        if (field.addressType === 'Mailing' && field.mailingSameAsResidential) {
            return [];
        }
    
        if (!noStreetNo) {
            newErrors = validatAddress(addressDetails, newErrors);
            newErrors = validateRoute(addressDetails, newErrors);
        } else {
            newErrors = validatNoStreetNoAddress(addressDetails, newErrors);
            newErrors = validateTownship(addressDetails, newErrors);
        }
    
        newErrors = validateUnitType(addressDetails, newErrors);
        if (!addressDetails.municipality) {
            newErrors = newErrors.filter(error => error.id !== 'municipality');
            newErrors.push({id: 'municipality', message: `${lang==='en' ? 'Municipality is missing': 'La municipalité manque'}`});
        }
        if (!addressDetails.postalCode) {
            newErrors = newErrors.filter(error => error.id !== 'postalCode');
            newErrors.push({id: 'postalCode', message: `${lang==='en' ? 'Postal code is missing': 'Le code postal est manquant'}`});
        }
        if (!addressDetails.province) {
            newErrors = newErrors.filter(error => error.id !== 'province');
            newErrors.push({id: 'province', message: `${lang==='en' ? 'Province is missing': 'La province manque'}`});
        }
        //setFiltefield.Valid = newErrors.length === 0;
        setErrors(newErrors);
        return newErrors;
    }

    useEffect(() => {
        if (addressDetails?.postalCode && noStreetNo) {
            setValue(addressDetails.postalCode);
        }
        return () => {
            console.log(`clear timeouts: ${timeoutIds}`);
            if (timeoutIds.length) {
                timeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
            }
        }
    }, []);
    
    useEffect(() => {
        if (field.Address && addressDetails && JSON.stringify(field.Address) !== JSON.stringify(addressDetails)) {
            setAddressDetails(field.Address);
        }
    }, [field.Address]);
    
    useEffect(()=>{
        if(!profile){
            //setFieldValue('');
            setSearchingAddress('');
            setValue('');
            setAddressDetails(null);
        } else if (field.addressType === 'Mailing' && field.mailingSameAsResidential) {
                const add = structuredClone(profile?.address) as Address;
                setAddressDetails(add);
                setNoStreeNo(add?.isNoStreetNoAddress);
        }
    },[profile]);

    useEffect(() => {
        if (!loading && !error && data) {
            if (data.mailAddressChoice?.addressDetails || data.mailAddressChoice?.length === 1) {
                const searchedAddress = data.mailAddressChoice?.addressDetails || data.mailAddressChoice?.[0];
                const displayAddress = getAddress({...searchedAddress, isDetailAddress: true, isNoStreetNoAddress: noStreetNo}, false, lang);
                setupAddressDetails(searchedAddress, noStreetNo);
                setValue(displayAddress);
                setFieldValue(displayAddress);
                //callbackParent();
            // } else if (data.mailAddressChoice.constructor === Array && data.mailAddressChoice.length) {
            //     const address:Address = data.mailAddressChoice[0];
            //     const addressVal = getAddress({...addressDetails, isNoStreetNoAddress: true}, false, lang); // Utils.getAddressStringFromDetails(address, true, lang);
            //     setupAddressDetails(address, true);
            //     setValue(addressVal);
            //     setFieldValue(addressVal);
            } else {
                setValue(addressString);
                setSuggestions(data.mailAddressChoice?.addressAlternatives ?? []);
            }
        }
    }, [data, error]);

    useEffect(() => {
        if (errors.length > 0) {
            setErrors([]);
        }
        if (addressDetails?.postalCode || addressDetails?.province) {
            const add = {...addressDetails, isNoStreetNoAddress: noStreetNo, isDetailAddress: true}
            const errs = validatAddressDetails();
            setFieldValid(errs.length === 0);
            if (errs.length > 0) {
                console.log("validaition failed");
                setValidationErrorMessage(errs)
            } else {
                const addressVal = addressDetails? 
                    getAddress({...addressDetails, isNoStreetNoAddress: noStreetNo}, false, lang) : 
                    getAddress(profile?.address, false, lang); 
                setFieldValue(addressVal);
                setValue(addressVal);
                setValidationErrorMessage([]);
            }
            if (addressDetails) setFieldAddress(add);
            if (field.addressType === 'Residential') {
                const element = findMatchedAddressElement(field);
                if (element && element.mailingSameAsResidential) {
                    const clone = structuredClone(add);
                    element.Address = add;
                    element.Value = getAddress(add, add.isNoStreetNoAddress, lang);
                }
            }
            callback(field.parent.parent.parent);
            //console.log(Utils.getAddress(addressDetails, true));
        } else if (field.addressType === 'Mailing' && !field.mailingSameAsResidential) {
            setValue('');
            setFieldValue('');
        }
    }, [addressDetails, lang, noStreetNo]);

    const handleNostreetNoChange = (e: ChangeEvent<HTMLInputElement>) =>{
        const checked = e.target.checked;
        removeErrorMessage(`searchInput`);
        setNoStreeNo(checked);
        if (checked) {
            setValue(addressDetails?.postalCode ?? profile?.address?.postalCode ?? '');
            setFieldValue(getAddress(addressDetails, false, lang));
        } else {
            const addressVal = addressDetails? getAddress({...addressDetails, isNoStreetNoAddress: false}, false, lang)
                : getAddress(profile?.address, false, lang);
            setFieldValue(addressVal);
            setValue(addressVal);
        }
        //callback(field.parent.parent.parent);
    };

    const handleSameMailingChange = (e: ChangeEvent<HTMLInputElement>) =>{
        const checked = e.target.checked;
        removeErrorMessage(`searchInput`);
        setSameMailing(checked);
        let add = null;
        if (checked) {
            const residential = findMatchedAddressElement(field);
            if (residential) {
                add = structuredClone(residential.Address) as Address | null;
                const details = getAddress(add, add?.isNoStreetNoAddress, lang);
                setAddressDetails(add);
                setFieldValue(details);
            }
        } else {
            setAddressDetails(null);
            setFieldValue('');
        }
        setFieldAddress(add);
        setSameAsResidential(checked);
        callback(field.parent.parent.parent);
    }

    const clearAddressDetails = () => {
        setAddressDetails(null);
        setNoStreeNo(null);
        setSuggestions([]);
        setFieldValid(true);
        setErrors([]);
        setFieldAddress(null);
        field.Touched = true;
        callbackParent();
    }   

    const handleSearchWords = (e: AutoCompleteChangeEvent) => {
        console.log(e);
        // if(e.value==""){
        //     return;
        // }
        const value = e.target.value;
        setValue(value);
        setFieldValue(value);
        if (!value || value.length < 3) {
            removeErrorMessage('searchInput');
            clearAddressDetails();
        }
        //callbackParent();
    }

    return (
        <div style={{marginBottom: '2.75rem'}}>
            <div className={`auto-suggestions-search ${!field.Valid? 'auto-suggestions-search-error' : ''}`}>
                <p><span>{noStreetNo? propts.noStreetNo[lang]: propts.streetNo[lang]}</span></p>
                <ErrorElement
                    display={!!errors.filter(err => err.id === 'searchInput').length} 
                    message={errors.filter(err => err.id === 'searchInput')?.[0]?.message}
                    id={field.LoadBindName + '_error'}
                />
                {/* @ts-ignore */}
                <AutoComplete
                    className={`auto-complete ${!field.Valid && !field.Value ? 'auto-complete-error' : ''}`}
                    field={fieldName} 
                    inputId={field.LoadBindName}
                    value={value || addressString} 
                    suggestions={suggestions}
                    completeMethod = {search}
                    onChange={handleSearchWords}
                    itemTemplate ={AddressSearchSuggestionTemplate}
                    onSelect={selectAddress}
                    disabled={field.ReadOnly || field.mailingSameAsResidential}
                />
            </div>
            {field.addressType!=='Mailing' && (
                <div className='ontario-checkboxes__item ontario-checkboxes__single-option'>
                    <input
                        className='ontario-checkboxes__input'
                        type='checkbox'
                        name='noStreetNoOption'
                        id={`fdIpt_no_streeNo_${field.Id}`}
                        title={propts.title[lang]}
                        checked={noStreetNo}
                        onChange={handleNostreetNoChange}
                        disabled={field.ReadOnly}
                    />
                    <label className='ontario-checkboxes__label' htmlFor={`fdIpt_no_streeNo_${field.Id}`}>{propts.title[lang]}</label>
                </div>
            )}

            {field.addressType==='Mailing' && (
                <div className='ontario-checkboxes__item ontario-checkboxes__single-option'>
                    <input
                        className='ontario-checkboxes__input'
                        type='checkbox'
                        name='sameMailing'
                        id={`fdIpt_same_mailing_${field.Id}`}
                        title={propts.sameMaling[lang]}
                        checked={sameMailing}
                        onChange={handleSameMailingChange}
                        disabled={field.ReadOnly}
                    />
                    <label className='ontario-checkboxes__label' htmlFor={`fdIpt_same_mailing_${field.Id}`}>{propts.sameMaling[lang]}</label>
                </div>
            )}

            {(!field.addressType || field.addressType==='Residential' || (field.addressType==='Mailing' && ((field.mailingSameAsResidential && field.showMailingDetails) || !field.mailingSameAsResidential))) && (
                <div className='address-detail-container'>
                    <AddressDetails
                        id={field.Id} 
                        lang={lang} 
                        noStreetNoAddress={noStreetNo}
                        setAddressDetails={setAddressDetails}
                        errors={errors}
                        addressDetails={addressDetails ?? field.Address}
                        readOnly={field.ReadOnly || (field.addressType==='Mailing' && ((field.mailingSameAsResidential && field.showMailingDetails) || !field.mailingSameAsResidential))}
                    />
                </div>
            )}
        </div>
    )
}

export default AddressSearchComponent;